import { ResultsetItem } from '@interfaces/resultset-item';
import { SimulationSetValidationErrorType } from '@interfaces/simulation-set-errors';
import { countElements } from '@helpers/counter';
import { sharedRegex } from '@helpers/spread/shared-regex';

const ValidationErrorMessages: { [key in SimulationSetValidationErrorType]: string } = {
  'mnemonic in db':
    'The same Mnemonic for a different combination of baseline, stress and dataset has already been used.',
  'invalid mnemonic': 'The mnemonic should contain only alphanumeric, “>” and “_”.',
  'duplicate mnemonic': 'This mnemonic is duplicated in the Simulation builder',
  'missing dataset': ''
};

/**
 * Validator class for simulation sets using the Chain of delegation pattern
 *
 * @example
 *     this.simulationSetState.forEach(rs => {
 *        const validator = new SimulationSetValidator(rs, this.simulationSetState, this.datasetMap, identifiers)
 *        .validateCharactersInMnemonic()
 *        .validateDuplicateMnemonic()
 *        .validateMnemonicInDB()
 *        .validateDataset();
 *        rs.ValidationError = validator.resultset.ValidationError;
 *    });
 */
export class SimulationSetValidator {
  private _resultset: ResultsetItem;

  public get resultset(): ResultsetItem {
    return this._resultset;
  }

  private _simulationSet: ResultsetItem[];
  private _datasetMap: Map<Number, any>;
  private _identifiers: ResultsetItem[];

  constructor(
    resultset: ResultsetItem,
    simulationSet: ResultsetItem[],
    datasetMap: Map<Number, any>,
    identifiers: ResultsetItem[]
  ) {
    this._resultset = resultset;
    this._resultset.ValidationError = null;
    this._simulationSet = simulationSet;
    this._datasetMap = datasetMap;
    this._identifiers = identifiers;
  }

  /**
   * Add the error to the resultset object created with the validator
   */
  private addError(type: SimulationSetValidationErrorType) {
    this._resultset = {
      ...this._resultset,
      ValidationError: {
        type,
        message: ValidationErrorMessages[type]
      }
    };
  }

  /**
   * Validate the dataset if it still exists, otherwise pass the object to the next validator
   */
  public validateDataset(): SimulationSetValidator {
    if (this._resultset.ValidationError) {
      return this;
    } else {
      if (this.isDatasetInactiveValidator(this._resultset)) {
        this.addError(SimulationSetValidationErrorType.MissingDataset);
      }
      return this;
    }
  }

  /**
   * Validate if the mnemonic matches with the identifiers, otherwise pass the object to the next validator
   */
  public validateMnemonicInDB(): SimulationSetValidator {
    if (this._resultset.ValidationError) {
      return this;
    } else {
      if (this.isMnemonicAlreadyUsedInDBValidator(this._simulationSet, this._resultset)) {
        this.addError(SimulationSetValidationErrorType.MnemonicAlreadyInDB);
      }
      return this;
    }
  }

  /**
   * Validate if mnemonic is duplicate, otherwise pass the object to the next validator
   */
  public validateDuplicateMnemonic(): SimulationSetValidator {
    if (this._resultset.ValidationError) {
      return this;
    } else {
      if (this.isMnemonicDuplicateValidator(this._simulationSet, this._resultset.Mnemonic)) {
        this.addError(SimulationSetValidationErrorType.DuplicateMnemonic);
      }
      return this;
    }
  }

  /**
   * Validate if mnemonics has invalid characters, otherwise pass the object to the next validator
   */
  public validateCharactersInMnemonic(): SimulationSetValidator {
    if (this._resultset.ValidationError) {
      return this;
    } else {
      if (!this.hasValidCharactersInMnemonicValidator(this._resultset.Mnemonic)) {
        this.addError(SimulationSetValidationErrorType.InvalidMnemonic);
      }
      return this;
    }
  }

  /**
   * Tests if the resultset dataset id is active in the system
   */
  private isDatasetInactiveValidator(resultset: ResultsetItem) {
    return !this._datasetMap.has(resultset.DataSetID);
  }

  /**
   * Test a resultset against the collection of resultset identifiers to verify if the mnemonic is already in the database
   * and if it exists, verify if it can be reused
   * @param resultset The resultset containing the information to test
   */
  private isMnemonicAlreadyUsedInDBValidator(flattenedSet: ResultsetItem[], resultset: ResultsetItem) {
    const mnemonic = resultset.Mnemonic;
    const mnemonicMatch = this._identifiers.find(item => mnemonic.toLowerCase() === item.Mnemonic.toLowerCase());

    if (mnemonicMatch) {
      if (resultset.Is_Stress) {
        // For a stress we check the if the parent is the same.
        const baselineMatch = this._identifiers.find(item => mnemonicMatch.RunID_Used === item.RunID);
        const parent = flattenedSet.find(item => resultset.RunID_Used === item.RunID);
        // A baseline should keep the same RunID if the Scenario ID and Dataset ID remains the same
        if (baselineMatch && parent) {
          return !(
            resultset.DataSetID === mnemonicMatch.DataSetID &&
            resultset.Scenario_ID === mnemonicMatch.Scenario_ID &&
            baselineMatch.Scenario_ID === parent.Scenario_ID &&
            baselineMatch.DataSetID === parent.DataSetID
          );
        }
      } else {
        return !(
          resultset.DataSetID === mnemonicMatch.DataSetID && resultset.Scenario_ID === mnemonicMatch.Scenario_ID
        );
      }
    }
    return false;
  }

  /**
   * Check if mnemonic is duplicate in the collection of resultsets
   * @param simulationSet The flattened simulation set
   * @param mnemonic The mnemonic to test against duplicates
   */
  private isMnemonicDuplicateValidator(simulationSet: ResultsetItem[], mnemonic: string) {
    const counter = countElements(simulationSet, item => item.Mnemonic);
    return counter[mnemonic] > 1;
  }

  /**
   * Test if the mnemonic is valid using the validation rules for mnemonics
   */
  public hasValidCharactersInMnemonicValidator(mnemonic: string): boolean {
    return sharedRegex.mnemonicSimulationSet.test(mnemonic);
    // return mnemonic.match(sharedRegex.mnemonicSimulationSet) !== null;
  }
}
